import React from 'react';
import { defineMessages, injectIntl } from 'react-intl';
import classnames from 'classnames';
import { Link } from 'react-router';
import { connect } from 'react-redux';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import _ from 'lodash';

import {
    createOrder,
    orderCreated,
    orderSelected,
    toggleOrderSidebar,
    orderUpdated,
    orderSetInactive,
    orderSetActive,
} from '../../../actions/company';
import api from '../../../actions/api';
import OrderTextInput from './OrderTextInput';
import OrderSidebarElement from './OrderSidebarElement';

const messages = defineMessages({
  ALL_EVENTS: {
    id: 'OrderSidebar.ALL_EVENTS',
    defaultMessage: 'Alle Ereignisse',
  },
  SHOW_ACTIVE: {
    id: 'OrderSidebar.SHOW_ACTIVE',
    defaultMessage: 'Nur aktive Projekte',
  },
  SHOW_ALL: {
    id: 'OrderSidebar.SHOW_ALL',
    defaultMessage: 'Alle Projekte anzeigen',
  },
  NEW_PROJECT: {
    id: 'OrderSidebar.NEW_PROJECT',
    defaultMessage: 'Neues Projekt',
  },
  PROJECTS: { id: 'OrderSidebar.PROJECTS', defaultMessage: 'Projekte' },
  SEARCH_PROJECTS: { id: 'OrderSidebar.SEARCH_PROJECTS', defaultMessage: 'Projekte durchsuchen...' },
  SHOW_ALL_SHORT: { id: 'OrderSidebar.SHOW_ALL_SHORT', defaultMessage: 'Alle Projekte' },
  SHOW_ONLY_ACTIVE_SHORT: { id: 'OrderSidebar.SHOW_ONLY_ACTIVE_SHORT', defaultMessage: 'Laufende Projekte' },
  TOTAL_OVERVIEW: { id: 'OrderSidebar.TOTAL_OVERVIEW', defaultMessage: 'Gesamtübersicht' },
  NO_PROJECTS_FOUND: { id: 'OrderSidebar.NO_PROJECTS_FOUND', defaultMessage: 'Keine Projekte gefunden' },
  INFO_TOOLTIP: { id: 'OrderSidebar.INFO_TOOLTIP', defaultMessage: 'Sie können Gespräche aus dem Aktivitätenprotokoll einem Projekt zuordnen. Es können unterschiedliche Gespräche zugeordnet werden. Automatisch wird das aktuelle Datum und die Uhrzeit vergeben, Sie können entweder dahinter etwas schreiben oder ihren Text schreiben, indem Sie das Datum mit der Uhrzeit herauslöschen. Ist der Vorgang beendet, können Sie das Projekt abschließen.' },
  FILTER_ALL: { id: 'OrderSidebar.FILTER_ALL', defaultMessage: 'Filter: Zeige alle Projekte' },
  FILTER_ACTIVE: { id: 'OrderSidebar.FILTER_ACTIVE', defaultMessage: 'Filter: Zeige nur aktive Projekte' },
});

@connect(
  (state) => {
    const { orderList, customerSingle: { customer }, orderSidebarOpen } = state;
    const customerId = customer && customer.id;
    const companyId = customer && customer.company_id;
    return { orderList, customerId, companyId, orderSidebarOpen };
  },
  dispatch => ({
    createOrder: (companyId, customerId, name) => {
      return createOrder(companyId, customerId, { name })
          .then((order) => {
            dispatch(orderCreated(order));
          });
    },
    selectOrder: (id) => {
      dispatch(orderSelected(id));
    },
    toggleOrderSidebar: () => dispatch(toggleOrderSidebar()),
    toggleOrderActive: (companyId, customerId, order) => {
      if (order.active) {
        orderSetInactive(companyId, customerId, order.id).then(newO => dispatch(orderUpdated(newO)));
      } else {
        orderSetActive(companyId, customerId, order.id).then(newO => dispatch(orderUpdated(newO)));
      }
    },
    updateOrder: (companyId, customerId, order, name) => {
      api.put({ url: `/api/company/${companyId}/customer/${customerId}/order/${order.id}/name`, data: { name } })
      .then((newOrder) => dispatch(orderUpdated(newOrder)));
    },
  })
)

class OrderSidebar extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      sidebarActive: false,
      showOnlyActive: false,
      searchQuery: "",
    };
  }

  _toggleActive() {
    this.setState({ sidebarActive: !this.state.sidebarActive });
  }

  handleLinkClick = () => {
    if (window.innerWidth <= 1100 && this.props.close) {
      this.props.close();
    }
  };

  render() {
    const {
      params,
      selectedOrder,
      middleHeight,
      active,
      orderList,
      close,
      customerId,
      companyId,
      intl: { formatMessage },
    } = this.props;
    if (orderList.isFetching) {
      return null;
    }
    let orders = (this.props.orderList.results || []).filter(order => order.id !== -1);
    const { showOnlyActive, searchQuery } = this.state;
    if (showOnlyActive) {
      orders = orders.filter(o => o.active);
    }
    
    // Deep Search Logic
    if (searchQuery && searchQuery.trim() !== '') {
      const q = searchQuery.toLowerCase().trim();
      orders = orders.filter(o => {
        // 1. Check Project Name
        if (o.name && o.name.toLowerCase().includes(q)) return true;
        
        // 2. Check Notes (Events)
        if (o.notes && o.notes.length > 0) {
          const notesMatch = o.notes.some(note => 
            note.text && note.text.toLowerCase().includes(q)
          );
          if (notesMatch) return true;
        }
        
        // 3. Check Calls
        if (o.calls && o.calls.length > 0) {
          const callsMatch = o.calls.some(call => 
            (call.transcript && call.transcript.toLowerCase().includes(q)) ||
            (call.caller_name && call.caller_name.toLowerCase().includes(q)) ||
            (call.caller_number && call.caller_number.toLowerCase().includes(q))
          );
          if (callsMatch) return true;
        }
        
        return false;
      });
    }
    
    orders = _.sortBy(orders, 'name');
    
    return (
      <div
        className={classnames('order-sidebar', { active })}
      >
        <div className="orders-wrapper" style={{ height: middleHeight - 110, position: 'relative' }}>
          
          <div className="header" style={{ padding: '0 0 10px 0', borderBottom: '1px solid var(--panel-border)', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ margin: 0, fontSize: '18px', color: 'var(--text-color)', fontWeight: 700 }}>{formatMessage(messages.PROJECTS)}</span>
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip id="projects-tooltip" style={{ maxWidth: 300, textAlign: 'left', fontSize: 12 }}>
                    {formatMessage(messages.INFO_TOOLTIP)}
                  </Tooltip>
                }
              >
                <span style={{ 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  width: 16, 
                  height: 16, 
                  borderRadius: '50%', 
                  background: 'var(--primary-color)', 
                  color: 'white', 
                  fontSize: 11, 
                  fontWeight: 'bold', 
                  cursor: 'help',
                  marginLeft: 8
                }}>?</span>
              </OverlayTrigger>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <a 
                href="#" 
                onClick={(e) => { e.preventDefault(); this.setState({ showOnlyActive: !showOnlyActive }); }}
                style={{ fontSize: '12px', color: 'var(--primary-color)', textDecoration: 'none', fontWeight: 500 }}
              >
                {showOnlyActive ? formatMessage(messages.SHOW_ALL) : formatMessage(messages.SHOW_ACTIVE)}
              </a>
            </div>
          </div>

          <div style={{ marginBottom: '16px', position: 'relative' }}>
            <i className="fa fa-search" style={{ position: 'absolute', left: '12px', top: '12px', color: 'var(--text-muted)' }} aria-hidden="true"></i>
            <input 
              type="text" 
              placeholder={formatMessage(messages.SEARCH_PROJECTS)} 
              aria-label={formatMessage(messages.SEARCH_PROJECTS)}
              value={this.state.searchQuery}
              onChange={(e) => this.setState({ searchQuery: e.target.value })}
              style={{ 
                width: '100%', 
                padding: '10px 10px 10px 32px', 
                borderRadius: '8px', 
                border: '1px solid var(--panel-border)', 
                background: 'rgba(0,0,0,0.02)',
                color: 'var(--text-color)',
                fontSize: '13px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>


          <div style={{ marginBottom: '16px' }}>
            <Link
              to={`/company/${params.companyId}/customer/${params.customerId}`}
              onClick={this.handleLinkClick}
              className={classnames('lh-btn', {
                'lh-btn-primary': selectedOrder === -1 || selectedOrder === -2,
                'lh-btn-default': selectedOrder !== -1 && selectedOrder !== -2
              })}
              style={{ 
                display: 'flex', 
                width: '100%', 
                whiteSpace: 'nowrap',
                boxSizing: 'border-box',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                height: '38px',
                cursor: 'pointer',
                fontSize: '13px',
                textDecoration: 'none'
              }}
            >
              <i className="fa fa-list" style={{ marginRight: '10px', width: '16px', textAlign: 'center' }} aria-hidden="true"></i> 
              <span style={{ display: 'inline-block' }}>
                {formatMessage(messages.TOTAL_OVERVIEW)}
              </span>
            </Link>
          </div>

          <OrderTextInput
            placeholder={"+ " + formatMessage(messages.NEW_PROJECT)}
            className="new-order new-order-top"
            onSave={(name) => this.props.createOrder(companyId, customerId, name)}
          />

          <ul className="project-list" style={{ padding: 0, margin: 0, listStyle: 'none' }}>
            {orders.length === 0 && searchQuery ? (
              <li style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
                <i className="fa fa-search" style={{ fontSize: '24px', marginBottom: '8px', opacity: 0.5, display: 'block' }}></i>
                {formatMessage(messages.NO_PROJECTS_FOUND)}
              </li>
            ) : (
              orders.map(result => (
                <OrderSidebarElement
                  key={result.id}
                  to={`/company/${params.companyId}/customer/${params.customerId}/order/${result.id}`}
                  order={result}
                  onClick={this.handleLinkClick}
                  className={classnames('project-card', {
                    active: result.id === selectedOrder,
                  })}
                  onRename={(name) => this.props.updateOrder(companyId, customerId, result, name)}
                  onToggleActive={() => this.props.toggleOrderActive(companyId, customerId, result)}
                />
              ))
            )}
          </ul>

        </div>
      </div>
    );
  }
}

export default injectIntl(OrderSidebar);
